import { useEffect, useState, useCallback } from 'react';
import { PageLayout } from '@/components/PageLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, Send, RefreshCw, AlertTriangle, CheckCircle2, XCircle, MinusCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_API_URL;

const STATUS_BADGE = {
  sent: { label: 'Gönderildi', cls: 'bg-emerald-100 text-emerald-800', Icon: CheckCircle2 },
  failed: { label: 'Başarısız', cls: 'bg-red-100 text-red-800', Icon: XCircle },
  skipped: { label: 'Atlandı', cls: 'bg-amber-100 text-amber-800', Icon: MinusCircle },
  queued: { label: 'Sırada', cls: 'bg-blue-100 text-blue-800', Icon: RefreshCw },
};

const REASON_LABEL = {
  sms_consent_false: 'İzin yok',
  phone_missing: 'Telefon yok',
  user_inactive: 'Kullanıcı pasif',
  provider_not_configured: 'Sağlayıcı hazır değil',
  provider_timeout: 'Sağlayıcı zaman aşımı',
};

export const AdminSmsLogs = () => {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, logsRes] = await Promise.all([
        axios.get(`${API_URL}/api/sms/stats?days=30`, { withCredentials: true }),
        axios.get(`${API_URL}/api/sms/logs?days=30&limit=200${statusFilter ? `&status=${statusFilter}` : ''}`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setLogs(logsRes.data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'SMS verileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error('Mesaj boş olamaz');
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/sms/send`,
        { message: message.trim() },
        { withCredentials: true }
      );
      const { queued, total, skipped_consent, skipped_phone, skipped_inactive, provider_configured } = res.data;
      toast.success(
        `${queued}/${total} SMS sıraya alındı. Atlandı: izin=${skipped_consent}, telefon=${skipped_phone}, pasif=${skipped_inactive}` +
        (provider_configured ? '' : ' • Sağlayıcı yapılandırılmamış!')
      );
      setMessage('');
      setTimeout(fetchAll, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'SMS gönderimi başlatılamadı');
    } finally {
      setSending(false);
    }
  };

  const fmt = (iso) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
    } catch (_e) {
      return iso;
    }
  };

  return (
    <PageLayout>
      <div className="max-w-6xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F172A]" data-testid="sms-logs-page-title">
            SMS Gönderim Sonuçları
          </h1>
          <p className="text-base text-[#595959] mt-2">
            Gönderim durumlarını izleyin. SMS yalnızca izinli kullanıcılara gönderilir.
          </p>
        </div>

        {/* Provider config warning */}
        {stats && !stats.provider_configured && (
          <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="sms-provider-warning">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div className="text-sm text-amber-900">
              <p className="font-medium">Netgsm sağlayıcısı yapılandırılmamış</p>
              <p className="text-amber-700 mt-0.5">
                SMS'ler kuyruğa alınır ancak gönderim başarısız olur. Lütfen <code className="bg-amber-100 px-1 rounded">NETGSM_USERNAME</code>, <code className="bg-amber-100 px-1 rounded">NETGSM_PASSWORD</code> ve <code className="bg-amber-100 px-1 rounded">NETGSM_MSGHEADER</code> ortam değişkenlerini ayarlayın.
              </p>
            </div>
          </div>
        )}

        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {['sent', 'failed', 'skipped', 'queued'].map((k) => {
            const cfg = STATUS_BADGE[k];
            const Icon = cfg.Icon;
            return (
              <Card key={k} className="border border-[#E5E5E5]" data-testid={`sms-stat-${k}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-[#595959]">{cfg.label}</p>
                      <p className="text-2xl font-bold text-[#1A1A2E]">{stats?.totals?.[k] ?? 0}</p>
                    </div>
                    <Icon className="w-6 h-6 text-[#999]" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Skip reasons breakdown */}
        {stats?.skip_reasons && Object.keys(stats.skip_reasons).length > 0 && (
          <Card className="border border-[#E5E5E5]" data-testid="sms-skip-reasons">
            <CardHeader>
              <CardTitle className="text-base text-[#2563EB]">Atlama Nedenleri (30 gün)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {Object.entries(stats.skip_reasons).map(([reason, count]) => (
                  <Badge key={reason} variant="secondary" className="text-xs" data-testid={`sms-reason-${reason}`}>
                    {REASON_LABEL[reason] || reason}: <span className="ml-1 font-bold">{count}</span>
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Send SMS form */}
        <Card className="border border-[#E5E5E5]" data-testid="sms-send-card">
          <CardHeader>
            <CardTitle className="text-xl text-[#0F172A] flex items-center gap-2">
              <MessageSquare className="w-5 h-5" /> Toplu SMS Gönder
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSend} className="space-y-3">
              <div>
                <Label className="text-sm text-[#595959]">Mesaj</Label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Aidat borcunuz bulunmaktadır. Son ödeme tarihi: 10.05.2026"
                  rows={3}
                  maxLength={600}
                  data-testid="sms-message-input"
                />
                <p className="text-xs text-[#999] mt-1">
                  Mesaj başına işletme adı otomatik eklenir. {message.length}/600
                </p>
              </div>
              <Button
                type="submit"
                disabled={sending || !message.trim()}
                className="bg-[#2563EB] hover:bg-[#1E4ED8] text-white"
                data-testid="sms-send-button"
              >
                <Send className="w-4 h-4 mr-2" />
                {sending ? 'Sıraya alınıyor...' : 'İzinli Kullanıcılara Gönder'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Logs table */}
        <Card className="border border-[#E5E5E5]" data-testid="sms-logs-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-xl text-[#0F172A]">Son SMS Kayıtları</CardTitle>
            <div className="flex items-center gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="h-9 rounded-md border border-[#E5E5E5] bg-white px-3 text-sm"
                data-testid="sms-logs-filter"
              >
                <option value="">Tümü</option>
                <option value="sent">Gönderildi</option>
                <option value="failed">Başarısız</option>
                <option value="skipped">Atlandı</option>
                <option value="queued">Sırada</option>
              </select>
              <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading} data-testid="sms-logs-refresh">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {logs.length === 0 ? (
              <p className="text-sm text-[#999] text-center py-8" data-testid="sms-logs-empty">
                Henüz SMS kaydı yok.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-[#595959] border-b border-[#E5E5E5]">
                    <tr>
                      <th className="text-left py-2 px-2">Durum</th>
                      <th className="text-left py-2 px-2">Telefon</th>
                      <th className="text-left py-2 px-2">Mesaj</th>
                      <th className="text-left py-2 px-2">Sebep</th>
                      <th className="text-left py-2 px-2">Tarih</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => {
                      const cfg = STATUS_BADGE[log.status] || STATUS_BADGE.failed;
                      return (
                        <tr key={log.id} className="border-b border-[#F0F0F0]" data-testid={`sms-log-row-${log.id}`}>
                          <td className="py-2 px-2">
                            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded ${cfg.cls}`}>
                              {cfg.label}
                            </span>
                          </td>
                          <td className="py-2 px-2 font-mono text-xs">{log.phone || '-'}</td>
                          <td className="py-2 px-2 max-w-md truncate" title={log.message}>{log.message}</td>
                          <td className="py-2 px-2 text-xs text-[#595959]">{REASON_LABEL[log.reason] || log.reason || '-'}</td>
                          <td className="py-2 px-2 text-xs text-[#999]">{fmt(log.created_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
};
